import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {APIResponse} from '@data/api-response';
import {firstValueFrom} from 'rxjs';

const SUPPORTED_BRANDS = new Set(['itm', 'cah']);

@Injectable({providedIn: 'root'})
export class BrandService {
  currentBrand = '';
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private themeLoaded = false;
  private imageMap = new Map<string, string>();
  private desktopLogoUrl: string | null = null;
  private mobileLogoUrl: string | null = null;

  private readonly MANUFACTURER_MESSAGES_KEY = 'manufacturerMessages';

  getBrandFromUrl(): string {
    const path = window.location.origin;
    const brand = `${environment.BRAND_MAP[path]}`;
    return brand || 'cah';
  }

  /**
   * Called by ThemeInitGuard before brand routes activate.
   * Waits for apigee-jwt (SMP-login-web delay), then loads theming.
   * On any failure navigates to /error and throws (so the guard cancels activation).
   */
  async loadThemeOrNavigateToError(maxWaitMs = 6000, intervalMs = 100): Promise<void> {
    if (this.themeLoaded) return;
    if (this.isOnErrorRoute()) return; // prevent loops

    const brand = this.getBrandFromUrl();
    this.currentBrand = brand;

    // Try local theme files if on localhost; fall back to GCP/API on error
    if (this.shouldUseLocalThemeAssets()) {
      try {
        const themeUrl = `/assets/themes/${brand}/theme.css`;
        const res = await fetch(themeUrl);
        if (res.ok) {
          this.applyLocalCss(brand);
          this.applyLocalLogoFallbacks(brand);
          this.loadLocalManufacturerMessages();
          this.themeLoaded = true;
          return;
        }
      } catch (e) {
        console.warn(`Local theme failed for brand "${brand}". Falling back to GCP/API.`, e);
      }
    }

    const jwtReady = await this.waitForApigeeJwt(maxWaitMs, intervalMs);
    if (!jwtReady) {
      await this.navigateToError('apigee-jwt not available in time');
      throw new Error('apigee-jwt missing');
    }

    const url = `${environment.apiBaseUrl}/assets`;

    try {
      const res: any = await firstValueFrom(this.http.get<APIResponse<any[]>>(url));

      // Accept APIResponse { code: 200 }
      const success = res?.code === 200;
      if (!success || !Array.isArray(res?.data)) {
        throw new Error('Assets API non-success');
      }

      // ---- CSS ----
      const cssFiles = res.data.filter(
        (f: any) => (f.fileType ?? '').toLowerCase() === 'text/css'
      );
      if (cssFiles.length === 0) {
        throw new Error('No CSS in payload');
      }
      this.applyCss(cssFiles);

      // ---- Images ----
      const images = res.data.filter(
        (f: any) => (f.fileType ?? '').toLowerCase().startsWith('image/')
      );
      this.prepareImages(images);

      //JSON assets (errorMessages.json)
      const jsonFiles = res.data.filter(
        (f: any) => (f.fileType ?? '').toLowerCase() === 'application/json'
      );
      this.processJsonAssets(jsonFiles);

      this.themeLoaded = true;
    } catch (err) {
      console.error('Theme load failed', err);
      await this.navigateToError(err);
      throw err;
    }
  }

  // ---- Logo getters used by Header ----
  getDesktopLogoUrl(): string | null {
    if (this.desktopLogoUrl) return this.desktopLogoUrl;
    const found = this.findByBaseNameInsensitive('logo.png');
    this.desktopLogoUrl = found ?? null;
    return this.desktopLogoUrl;
  }

  getMobileLogoUrl(): string | null {
    if (this.mobileLogoUrl) return this.mobileLogoUrl;
    const found = this.findByBaseNameInsensitive('mobile-logo.png');
    this.mobileLogoUrl = found ?? null;
    return this.mobileLogoUrl;
  }

  // --------------- helpers ----------------
  private async navigateToError(reason?: unknown) {
    console.error('[BrandService] Redirecting to /error. Reason:', reason);
    if (!this.isOnErrorRoute()) {
      await this.router.navigateByUrl('/error', {replaceUrl: true});
    }
  }

  private isOnErrorRoute(): boolean {
    try {
      return new URL(window.location.href).pathname.toLowerCase().startsWith('/error');
    } catch {
      return false;
    }
  }

  private async waitForApigeeJwt(maxWaitMs: number, intervalMs: number): Promise<boolean> {
    const start = Date.now();
    if (this.hasJwt()) return true;

    while (Date.now() - start < maxWaitMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      if (this.hasJwt()) return true;
    }
    return false;
  }

  private hasJwt(): boolean {
    try {
      const token = sessionStorage.getItem('apigee-jwt');
      return !!token && token.length > 0;
    } catch {
      return false;
    }
  }

  private applyCss(cssFiles: any[]) {
    cssFiles.forEach((css, index) => {
      const blob = this.base64ToBlob(css.content, 'text/css');
      const url = URL.createObjectURL(blob);
      const linkId = `theme-css-${index}`;
      let link = document.getElementById(linkId) as HTMLLinkElement;

      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }

      link.removeAttribute('integrity');
      link.removeAttribute('crossorigin');
      link.href = url;
    });
  }

  private prepareImages(images: any[]) {
    this.imageMap.clear();
    this.desktopLogoUrl = null;
    this.mobileLogoUrl = null;

    images.forEach(img => {
      const full = (img.fileName || '').trim();
      const base = full.split('/').pop()?.trim().toLowerCase() || '';
      if (!base) return;

      const blob = this.base64ToBlob(img.content, img.fileType);
      const url = URL.createObjectURL(blob);

      this.imageMap.set(base, url);
      if (base === 'logo.png') this.desktopLogoUrl = url;
      if (base === 'mobile-logo.png') this.mobileLogoUrl = url;
    });
  }

  private findByBaseNameInsensitive(name: string): string | undefined {
    const target = name.toLowerCase();
    for (const [base, url] of this.imageMap.entries()) {
      if (base === target) return url;
    }
    return undefined;
  }

  private base64ToBlob(b64: string, type: string): Blob {
    const binary = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], {type});
  }

  // base64 -> UTF-8 text
  private base64ToText(b64: string): string {
    const binary = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
  }

  private shouldUseLocalThemeAssets(): boolean {
    const host = window.location.hostname;
    return host === 'localhost';
  }

  private applyLocalCss(brand: string): void {
    const basePath = '/assets/themes';
    const href = `${basePath}/${brand}/theme.css?v=${Date.now()}`;
    const linkId = 'theme-css-0';

    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    link.removeAttribute('integrity');
    link.removeAttribute('crossorigin');
    link.href = href;
  }

  private applyLocalLogoFallbacks(brand: string): void {

    const desktop = '/assets/images/brand-logo.png';
    const mobile = '/assets/images/CAH-mobile-logo.png';

    this.desktopLogoUrl = desktop;
    this.mobileLogoUrl = mobile;

    this.imageMap.clear();
    this.imageMap.set('logo.png', desktop);
    this.imageMap.set('mobile-logo.png', mobile);
  }

  async getManufacturerConstants(): Promise<void> {
    const cached = sessionStorage.getItem(this.MANUFACTURER_MESSAGES_KEY);
    if (cached) {
      return;
    }
    const url = `${environment.apiBaseUrl}/assets`;
    try {
      const res: any = await firstValueFrom(this.http.get<APIResponse<any[]>>(url));
      if (res?.code !== 200 || !Array.isArray(res?.data)) {
        throw new Error('Assets API non-success');
      }
      const jsonFiles = res.data.filter((file: any) => (file.fileType ?? '').toLowerCase() === 'application/json');
      this.processJsonAssets(jsonFiles);
    } catch (error) {
      console.error('Manufacturer Json load failed', error);
      throw error;
    }
  }

  private async loadLocalManufacturerMessages(): Promise<void> {
    const localJsonPath = 'assets/themes/itm/messages.json';
    try {
      const response = await fetch(localJsonPath);
      if (!response.ok) {
        console.warn('Message constants not found.');
        sessionStorage.setItem(this.MANUFACTURER_MESSAGES_KEY, JSON.stringify({}));
        return;
      }
      const rawText = await response.text();
      if (rawText.trim().startsWith('<!DOCTYPE html>')) {
        console.warn(`constants file not found.`);
        sessionStorage.setItem(this.MANUFACTURER_MESSAGES_KEY, JSON.stringify({}));
        return;
      }
      const data = JSON.parse(rawText);
      const messages = data?.['manufacturer-messages'];
      if (messages && typeof messages === 'object') {
        sessionStorage.setItem(this.MANUFACTURER_MESSAGES_KEY, JSON.stringify(messages));
      } else {
        sessionStorage.setItem(this.MANUFACTURER_MESSAGES_KEY, JSON.stringify({}));
      }
    } catch (error) {
      console.error('Unexpected native fetch breakdown', error);
      sessionStorage.setItem(this.MANUFACTURER_MESSAGES_KEY, JSON.stringify({}));
    }
  }

  private processJsonAssets(jsonFiles: any[]): void {
    for (const file of jsonFiles) {
      if (!file?.content) {
        continue;
      }
      try {
        const jsonText = this.base64ToText(file.content);
        const parsed = JSON.parse(jsonText);
        const messages = parsed?.['manufacturer-messages'];
        if (messages && typeof messages === 'object') {
          sessionStorage.setItem(this.MANUFACTURER_MESSAGES_KEY, JSON.stringify(messages));
          break;
        }
      } catch (error) {
        console.warn('Failed to parse json asset', error);
      }
    }
  }

  replaceManufacturerPlaceholders(message: string): string {
    if (!message) {
      return message;
    }
    const savedData = sessionStorage.getItem(this.MANUFACTURER_MESSAGES_KEY);
    if (!savedData) {
      return message;
    }
    const replacements = JSON.parse(savedData);
    return Object.entries(replacements).reduce(
      (updatedMessage, [placeholder, value]) =>
        updatedMessage.replaceAll(placeholder, String(value)), message);
  }

  replaceManufacturerPlaceholdersInArray(messages: string[]): string[] {
    if (!messages?.length) {
      return messages;
    }

    return messages.map(message =>
      this.replaceManufacturerPlaceholders(message)
    );
  }
}
